import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, LoginRequest } from '@/api/login/type';
import { api } from '@/api/login/api';
import {
    toastError,
    toastSuccess,
} from '@components/common/Toast/CustomToast.tsx';
import { createEncryptedStorage } from '@store/cryptoStorage';

declare global {
    interface Window {
        Kakao: any;
        google: any;
    }
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => {
            return {
                isAuthenticated: false,
                accessToken: null,
                expireTime: null,
                idx: null,
                name: null,
                email: null,
                role: null,
                isLoading: false,

                _hasHydrated: false,
                setHasHydrated: (state: boolean) =>
                    set({ _hasHydrated: state }),

                isUser: () => get().role === 1,

                // ── 토큰 직접 저장 (SNS 회원가입 완료 후 사용)
                setTokenFromLogin: data => {
                    set({
                        isAuthenticated: true,
                        accessToken: data.accessToken,
                        expireTime: data.expireTime,
                        idx: data.idx,
                        name: data.name,
                        email: data.email,
                        role: data.role,
                    });
                    toastSuccess('회원가입이 완료되었습니다. 환영합니다!');
                },

                login: async (credentials: LoginRequest) => {
                    set({ isLoading: true });
                    try {
                        const response = await api.login(credentials);
                        if (response.code === 0) {
                            const {
                                accessToken,
                                expireTime,
                                idx,
                                name,
                                email,
                                role,
                            } = response.data;
                            set({
                                isAuthenticated: true,
                                accessToken,
                                expireTime,
                                idx,
                                name,
                                email,
                                role,
                                isLoading: false,
                            });
                            toastSuccess('로그인에 성공했습니다.');
                            return { success: true };
                        } else {
                            set({ isLoading: false });
                            const errorMessage =
                                response.message || '로그인에 실패했습니다.';
                            toastError(errorMessage);
                            return { success: false, error: errorMessage };
                        }
                    } catch (error) {
                        set({ isLoading: false });
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : '로그인에 실패했습니다.';
                        return { success: false, error: errorMessage };
                    }
                },

                // ── ID/PW 로그인 (대행사)
                idLogin: async (userId: string, userPw: string) => {
                    set({ isLoading: true });
                    try {
                        const response = await api.idLogin(userId, userPw);
                        if (response.code === 0) {
                            const {
                                accessToken,
                                expireTime,
                                idx,
                                name,
                                email,
                                role,
                            } = response.data;
                            set({
                                isAuthenticated: true,
                                accessToken,
                                expireTime,
                                idx,
                                name,
                                email,
                                role,
                                isLoading: false,
                            });
                            toastSuccess('로그인에 성공했습니다.');
                            return { success: true };
                        } else {
                            set({ isLoading: false });
                            const errorMessage =
                                response.message || '로그인에 실패했습니다.';
                            toastError(errorMessage);
                            return { success: false, error: errorMessage };
                        }
                    } catch (error) {
                        set({ isLoading: false });
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : '로그인에 실패했습니다.';
                        return { success: false, error: errorMessage };
                    }
                },

                // ── 네이버 로그인
                naverLogin: async (accessToken: string) => {
                    set({ isLoading: true });
                    try {
                        const result = await api.naverLogin(accessToken);

                        console.log(result);
                        if (!result.success) {
                            set({ isLoading: false });
                            toastError(
                                result.error || '네이버 로그인에 실패했습니다.',
                            );
                            console.log(1);
                            return { success: false, error: result.error };
                        }

                        if (result.isJoined && result.snsInfo?.accessToken) {
                            const {
                                accessToken: token,
                                expireTime,
                                idx,
                                name,
                                email,
                                role,
                            } = result.snsInfo;
                            set({
                                isAuthenticated: true,
                                accessToken: token ?? null,
                                expireTime: expireTime ?? null,
                                idx: idx ?? null,
                                name: name ?? null,
                                email: email ?? null,
                                role: role ?? null,
                                isLoading: false,
                            });
                            console.log(2);
                            toastSuccess('네이버 로그인에 성공했습니다.');
                            return { success: true, isJoined: true };
                        }

                        set({ isLoading: false });
                        console.log(3);
                        return {
                            success: true,
                            isJoined: false,
                            snsInfo: result.snsInfo,
                        };
                    } catch (error) {
                        set({ isLoading: false });
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : '네이버 로그인에 실패했습니다.';
                        toastError(errorMessage);
                        console.log(4);
                        return { success: false, error: errorMessage };
                    }
                },

                // ── 카카오 로그인
                kakaoLogin: async (code: string) => {
                    set({ isLoading: true });
                    try {
                        const result = await api.kakaoLogin(code);

                        if (!result.success) {
                            set({ isLoading: false });
                            toastError(
                                result.error || '카카오 로그인에 실패했습니다.',
                            );
                            return { success: false, error: result.error };
                        }

                        if (result.isJoined && result.snsInfo?.accessToken) {
                            const {
                                accessToken,
                                expireTime,
                                idx,
                                name,
                                email,
                                role,
                            } = result.snsInfo;
                            set({
                                isAuthenticated: true,
                                accessToken: accessToken ?? null,
                                expireTime: expireTime ?? null,
                                idx: idx ?? null,
                                name: name ?? null,
                                email: email ?? null,
                                role: role ?? null,
                                isLoading: false,
                            });
                            toastSuccess('카카오 로그인에 성공했습니다.');
                            return { success: true, isJoined: true };
                        }

                        set({ isLoading: false });
                        return {
                            success: true,
                            isJoined: false,
                            snsInfo: result.snsInfo,
                        };
                    } catch (error) {
                        set({ isLoading: false });
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : '카카오 로그인에 실패했습니다.';
                        toastError(errorMessage);
                        return { success: false, error: errorMessage };
                    }
                },

                // ── 구글 로그인
                googleLogin: async (code: string) => {
                    set({ isLoading: true });
                    try {
                        const result = await api.googleLogin(code);

                        if (!result.success) {
                            set({ isLoading: false });
                            toastError(
                                result.error || '구글 로그인에 실패했습니다.',
                            );
                            return { success: false, error: result.error };
                        }

                        if (result.isJoined && result.snsInfo?.accessToken) {
                            const {
                                accessToken,
                                expireTime,
                                idx,
                                name,
                                email,
                                role,
                            } = result.snsInfo;
                            set({
                                isAuthenticated: true,
                                accessToken: accessToken ?? null,
                                expireTime: expireTime ?? null,
                                idx: idx ?? null,
                                name: name ?? null,
                                email: email ?? null,
                                role: role ?? null,
                                isLoading: false,
                            });
                            toastSuccess('구글 로그인에 성공했습니다.');
                            return { success: true, isJoined: true };
                        }

                        set({ isLoading: false });
                        return {
                            success: true,
                            isJoined: false,
                            snsInfo: result.snsInfo,
                        };
                    } catch (error) {
                        set({ isLoading: false });
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : '구글 로그인에 실패했습니다.';
                        toastError(errorMessage);
                        return { success: false, error: errorMessage };
                    }
                },

                logout: async () => {
                    if (
                        window.Kakao?.isInitialized() &&
                        window.Kakao?.Auth?.getAccessToken()
                    ) {
                        await window.Kakao.Auth.logout().catch(() => {});
                    }
                    if (window.google?.accounts?.id) {
                        window.google.accounts.id.disableAutoSelect();
                    }
                    set({
                        isAuthenticated: false,
                        accessToken: null,
                        expireTime: null,
                        idx: null,
                        name: null,
                        email: null,
                        role: null,
                    });
                    toastSuccess('로그아웃 되었습니다.');
                },

                setLoading: (loading: boolean) => set({ isLoading: loading }),

                checkTokenExpiry: () => {
                    const { expireTime, isAuthenticated } = get();
                    if (!isAuthenticated) return false;
                    if (!expireTime) {
                        get().logout();
                        toastError(
                            '토큰 정보가 없습니다. 다시 로그인해주세요.',
                        );
                        return false;
                    }
                    if (Date.now() + 5 * 60 * 1000 > expireTime) {
                        get().logout();
                        toastError(
                            '토큰이 만료되었습니다. 다시 로그인해주세요.',
                        );
                        return false;
                    }
                    return true;
                },

                checkTokenWarning: () => {
                    const { expireTime, isAuthenticated } = get();
                    if (!isAuthenticated || !expireTime) return;
                    const currentTime = Date.now();
                    if (
                        currentTime + 10 * 60 * 1000 > expireTime &&
                        currentTime < expireTime
                    ) {
                        toastError(
                            '토큰이 곧 만료됩니다. 작업을 저장해주세요.',
                        );
                    }
                },
            };
        },
        {
            name: 'auth-storage',
            storage: createEncryptedStorage(),
            onRehydrateStorage: () => state => {
                state?.setHasHydrated(true);
            },
            partialize: state => ({
                isAuthenticated: state.isAuthenticated,
                accessToken: state.accessToken,
                expireTime: state.expireTime,
                idx: state.idx,
                name: state.name,
                email: state.email,
                role: state.role,
            }),
        },
    ),
);
